import { Dayjs } from 'dayjs';
import { getDollarValue } from 'lib/api/cmf';
import { getUfValue } from 'lib/api/cmf';
import { useDispatch } from 'react-redux';

export type RiskPremiumRates = {
  variableRate: number | null;
  fixedRate: number | null;
  variableDiscount: number | null;
  fixedDiscount: number | null;
};

export interface Input {
  quotedAmount: number;
  quotedCurrency: 'CLP' | 'USD' | 'UF' | string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  rates?: RiskPremiumRates;
}

export interface Output {
  amountInCLP: number;
  financialYear: number;
  guaranteeAmount: number;
  effectiveVariableRate: number;
  effectiveFixedRate: number;
  variableComponent: number;
  fixedComponent: number;
  amount: number;
  variableDiscount: number;
  fixedDiscount: number;
  originalVariableRate: number;
  originalFixedRate: number;
}

const riskPremiumCalculator = () => {
  const dispatch = useDispatch();
  const { data: dollar } = getDollarValue(dispatch);
  const { data: uf } = getUfValue(dispatch);

  const calculate = (input: Input): Output => {
    const { quotedAmount, quotedCurrency, startDate, endDate, rates } = input;

    if (!startDate || !endDate)
      throw new Error('Se requiere una fecha de inicio y fin');

    if (!dollar?.value || !uf?.value)
      throw new Error('Valor de la moneda (UF o USD) no encontrado');

    const variableRate = rates?.variableRate;
    const fixedRate = rates?.fixedRate;
    const variableDiscount = rates?.variableDiscount ?? 0;
    const fixedDiscount = rates?.fixedDiscount ?? 0;

    if (!variableRate || !fixedRate)
      throw new Error('Tasas de riesgo no encontradas');

    let amountInCLP = 0;

    switch (quotedCurrency) {
      case 'CLP':
        amountInCLP = quotedAmount;
        break;
      case 'USD':
        amountInCLP = quotedAmount * dollar.value;
        break;
      case 'UF':
        amountInCLP = quotedAmount * uf.value;
        break;
      default:
        throw new Error('Moneda no soportada');
    }

    const days = Math.ceil(
      (new Date(endDate.toDate()).getTime() -
        new Date(startDate.toDate()).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    const financialYear = days / 360;
    const guaranteeAmount = amountInCLP * financialYear;
    const effectiveVariableRate = variableRate * (1 - variableDiscount / 100);
    const effectiveFixedRate = fixedRate * (1 - fixedDiscount / 100);
    const variableComponent = Math.floor(
      guaranteeAmount * (effectiveVariableRate / 100),
    );
    const fixedComponent = effectiveFixedRate * (uf?.value ?? 1);
    const amount = Math.floor(variableComponent + fixedComponent);

    return {
      amountInCLP,
      financialYear,
      guaranteeAmount,
      effectiveVariableRate,
      effectiveFixedRate,
      variableComponent,
      fixedComponent,
      amount,
      variableDiscount,
      fixedDiscount,
      originalVariableRate: variableRate,
      originalFixedRate: fixedRate,
    };
  };

  return { calculate };
};

export default riskPremiumCalculator;
